import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { BASE_URL } from './utils/config';
import { paths } from './routing/path';
import { isServerUnavailableError } from './utils/isServerUnavailableError';
import { maintenanceMode } from './utils/maintenanceMode';
import {
    VIEW_AS_BUYER_HEADER,
    getActiveViewAsBuyerUserId,
    notifyViewAsBuyerReadOnly,
} from './utils/viewAsBuyer';
import { markViewAsBuyerReadOnlyRejection } from './utils/viewAsBuyerReadOnlySignal';

const FORBIDDEN_STATUS = 403;

const identityInfrastructurePaths = ['api/auth/', 'api/users/getusersummary'];

function isIdentityInfrastructureRequest(
    config: InternalAxiosRequestConfig
): boolean {
    const requestUrl = (config.url ?? '').toLowerCase().replace(/^\//, '');
    return identityInfrastructurePaths.some((path) =>
        requestUrl.startsWith(path)
    );
}

const VIEW_AS_BUYER_READ_ONLY_EXCEPTION = 'ViewAsBuyerReadOnlyException';

function notifyWhenViewAsBuyerReadOnlyRejection(error: any): void {
    const exceptionType: string = error?.response?.data?.type ?? '';
    const isReadOnlyRejection =
        error?.response?.status === FORBIDDEN_STATUS &&
        getActiveViewAsBuyerUserId() !== null &&
        exceptionType.endsWith(VIEW_AS_BUYER_READ_ONLY_EXCEPTION);
    if (!isReadOnlyRejection) {
        return;
    }
    markViewAsBuyerReadOnlyRejection();
    notifyViewAsBuyerReadOnly();
}

declare module 'axios' {
    export interface AxiosRequestConfig<D = any> {
        // Set on requests (e.g. the maintenance health poll) whose failures
        // must not trigger the maintenance overlay.
        skipMaintenanceCheck?: boolean;
    }
}

export function createBaseAxiosInstance(): AxiosInstance {
    const instance = axios.create({
        baseURL: BASE_URL,
        withCredentials: true,
    });

    (instance as any).all = axios.all;
    (instance as any).spread = axios.spread;

    instance.interceptors.request.use(
        async (config: InternalAxiosRequestConfig) => {
            const token = localStorage.getItem('token');
            if (token) {
                config.headers.Authorization = 'Bearer ' + token;
            }
            const currentPracticeId = localStorage.getItem('currentPracticeId');
            if (currentPracticeId) {
                config.headers.practiceId = currentPracticeId;
            }
            const currentPracticeRegId = localStorage.getItem(
                'currentPracticeRegId'
            );
            if (currentPracticeRegId) {
                config.headers.practiceRegId = currentPracticeRegId;
            }
            const viewAsBuyerUserId = getActiveViewAsBuyerUserId();
            if (viewAsBuyerUserId && !isIdentityInfrastructureRequest(config)) {
                config.headers[VIEW_AS_BUYER_HEADER] = viewAsBuyerUserId;
            }
            return config;
        },
        (error) => {
            return error;
        }
    );

    instance.interceptors.response.use(
        (response) => {
            return response;
        },
        (error) => {
            notifyWhenViewAsBuyerReadOnlyRejection(error);
            if (isServerUnavailableError(error)) {
                maintenanceMode.setServerUnavailable(true);
            }
            if (error.response && error.response.status === 401 &&
                !window.location.pathname.startsWith('/login')) {
                localStorage.clear();
                window.location.href = window.location.origin + paths.public.login;
                // The page is navigating to login; keep in-flight requests
                // pending forever so they can't resolve with undefined and
                // crash reducers mid-redirect.
                return new Promise(() => {});
            }
            return error;
        }
    );

    return instance;
}
